// Mirror of Shared/Calculators/PaintCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "roomWidthFt", kind: "number", default: 12, min: 1, max: 200 },
  { key: "roomLengthFt", kind: "number", default: 14, min: 1, max: 200 },
  { key: "ceilingHeightFt", kind: "number", default: 8, min: 6, max: 20 },
  {
    key: "ceiling",
    kind: "select",
    defaultChoice: "no",
    choices: [
      { value: "no", set: { includeCeiling: 0 } },
      { value: "yes", set: { includeCeiling: 1 } },
    ],
  },
  { key: "doors", kind: "number", default: 1, min: 0, max: 20 },
  { key: "windows", kind: "number", default: 2, min: 0, max: 30 },
  { key: "coats", kind: "number", default: 2, min: 1, max: 4 },
  { key: "coveragePerGallon", kind: "number", default: 350, min: 100, max: 500 },
  { key: "pricePerGallon", kind: "number", default: 35, min: 0, max: 200 },
];

const DOOR_AREA = 21.0;
const WINDOW_AREA = 15.0;

function compute(i: Record<string, number>): Record<string, number> {
  const w = F.get(i, "roomWidthFt");
  const l = F.get(i, "roomLengthFt");
  const h = F.get(i, "ceilingHeightFt");
  const includeCeiling = F.get(i, "includeCeiling");
  const doors = F.get(i, "doors");
  const windows = F.get(i, "windows");
  const coats = F.get(i, "coats");
  const coverage = F.get(i, "coveragePerGallon");
  const price = F.get(i, "pricePerGallon");

  const wallArea = 2 * (w + l) * h;
  const ceilingArea = includeCeiling >= 1 ? w * l : 0;
  const deductions = doors * DOOR_AREA + windows * WINDOW_AREA;
  const paintable = Math.max(0, wallArea + ceilingArea - deductions);
  const totalToCover = paintable * coats;
  const gallonsExact = coverage > 0 ? totalToCover / coverage : 0;
  const gallons = F.ceilUnits(gallonsExact);
  const cost = gallons * price;

  return {
    wallArea: F.round(wallArea, 2),
    ceilingArea: F.round(ceilingArea, 2),
    paintable: F.round(paintable, 2),
    totalToCover: F.round(totalToCover, 2),
    gallonsExact: F.round(gallonsExact, 2),
    gallons,
    cost: F.round(cost, 2),
  };
}

function present(i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const coats = F.get(i, "coats");
  const gallons = F.get(r, "gallons");
  return [
    { label: "Paintable area", value: `${F.dec(F.get(r, "paintable"))} sq ft` },
    { label: `Total over ${F.dec(coats, 0)} coats`, value: `${F.dec(F.get(r, "totalToCover"))} sq ft` },
    { label: "Gallons to buy", value: `${F.intStr(gallons)} ${F.plural(gallons, "gallon", "gallons")}`, highlight: true },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — paint only, excludes primer, tools & labor" },
  ];
}

export const paint: CalcDef = { slug: "paint-calculator", fields, compute, present };
